<template>
  <div class="row payments-list">
    <!-- Payments Table -->
    <div>
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <h6 class="mb-0">
          {{ $t("Payments List") }}
          <span class="badge bg-primary ms-2"
            >{{ pagination?.totalCount || 0 }} {{ $t("payments") }}</span
          >
        </h6>
        <button
          @click="exportData"
          class="btn btn-outline-success btn-sm"
          :disabled="loading || payments.length === 0"
        >
          <i class="fas fa-download"></i> {{ $t("Export") }}
        </button>
      </div>
      <div class="card-body">
        <ContentSkeleton v-if="loading" variant="table" :rows="8" :cols="6" />
        <div v-else-if="payments.length === 0" class="text-center py-4">
          <i class="fas fa-credit-card fa-3x text-muted mb-3"></i>
          <p class="text-muted">{{ $t("No payments found") }}</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>{{ $t("Proform") }}</th>
                <th>{{ $t("Contact") }}</th>
                <th>{{ $t("Amount") }}</th>
                <th>Applied</th>
                <th>{{$t("Remaining to apply")}}</th>
                <th>{{ $t("Status") }}</th>
                <th>{{ $t("Payment Date") }}</th>
                <th>Bank Account</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="paymentGroup in payments">
                <!-- Main proform row -->
                <tr :key="`proform-${paymentGroup._id}`" class="hover:bg-light">
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <i
                        v-if="
                          paymentGroup.shippings &&
                          paymentGroup.shippings.length > 0
                        "
                        class="fas fa-folder text-warning me-2"
                        :title="$t('Has shippings')"
                      ></i>
                      <i
                        v-else
                        class="fas fa-file-invoice text-primary me-2"
                        :title="$t('Proform')"
                      ></i>
                      <span
                        @click="viewProform(paymentGroup)"
                        class="text-primary fw-bold cursor-pointer"
                        style="cursor: pointer; text-decoration: underline"
                        :title="`Click to view ${paymentGroup.proformName} details`"
                      >
                        {{ paymentGroup.proformName }}
                      </span>
                      <span
                        v-if="
                          paymentGroup.shippings &&
                          paymentGroup.shippings.length > 0
                        "
                        class="badge bg-warning text-dark ms-2"
                      >
                        {{ paymentGroup.shippings.length }} {{ $t("shipping(s)") }}
                      </span>
                      <button
                        @click="viewProform(paymentGroup)"
                        class="btn btn-sm btn-outline-primary"
                        title="View proform details"
                      >
                        <i class="fas fa-external-link-alt"></i>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <span
                        @click="viewContact(paymentGroup.contact)"
                        class="text-primary fw-bold cursor-pointer"
                        style="cursor: pointer; text-decoration: underline"
                        :title="`Click to view ${getContactName(
                          paymentGroup.contact
                        )} details`"
                      >
                        {{ getContactName(paymentGroup.contact) }}
                      </span>
                      <button
                        v-if="paymentGroup.contact && paymentGroup.contact._id"
                        @click="viewContact(paymentGroup.contact)"
                        class="btn btn-sm btn-outline-info"
                        title="View contact details"
                      >
                        <i class="fas fa-user"></i>
                      </button>
                    </div>
                  </td>
                  <td class="text-end">
                    <span class="text-primary fw-bold">
                      ${{ formatNumber(paymentGroup.totalAmount || 0) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <span class="text-success fw-bold">
                      ${{ formatNumber(paymentGroup.totalApplied || 0) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <span
                      :class="
                        paymentGroup.totalAmount - paymentGroup.totalApplied > 0
                          ? 'text-warning fw-bold'
                          : 'text-success fw-bold'
                      "
                    >
                      ${{
                        formatNumber(
                          (paymentGroup.totalAmount || 0) -
                            (paymentGroup.totalApplied || 0)
                        )
                      }}
                    </span>
                  </td>
                  <td>
                    <span
                      :class="getStatusClass(paymentGroup.status || 'pending')"
                    >
                      {{ getStatusLabel(paymentGroup.status || "pending") }}
                    </span>
                  </td>
                  <td>{{ formatDate(paymentGroup.latestPaymentDate) }}</td>
                  <td>
                    <span class="text-muted">-</span>
                  </td>
                  <td>
                    <span class="text-muted">-</span>
                  </td>
                </tr>

                <!-- Shipping rows (nested under proform) -->
                <tr
                  v-for="shipping in paymentGroup.shippings"
                  :key="shipping._id"
                  class="hover:bg-light bg-light"
                >
                  <td>
                    <div class="d-flex align-items-center gap-2 ms-4">
                      <i class="fas fa-ship text-info me-2"></i>
                      <span
                        @click="viewShipping(shipping)"
                        class="text-primary fw-bold cursor-pointer"
                        style="cursor: pointer; text-decoration: underline"
                        title="Click to view shipping details"
                      >
                        {{ shipping.proformName }}
                      </span>
                      <button
                        @click="viewShipping(shipping)"
                        class="btn btn-sm btn-outline-primary"
                        title="View shipping details"
                      >
                        <i class="fas fa-external-link-alt"></i>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <span
                        @click="viewContact(shipping.contact)"
                        class="text-primary fw-bold cursor-pointer"
                        style="cursor: pointer; text-decoration: underline"
                        :title="`Click to view ${getContactName(
                          shipping.contact
                        )} details`"
                      >
                        {{ getContactName(shipping.contact) }}
                      </span>
                      <button
                        v-if="shipping.contact && shipping.contact._id"
                        @click="viewContact(shipping.contact)"
                        class="btn btn-sm btn-outline-info"
                        title="View contact details"
                      >
                        <i class="fas fa-user"></i>
                      </button>
                    </div>
                  </td>
                  <td class="text-end">
                    <span class="text-primary fw-bold">
                      ${{ formatNumber(shipping.totalAmount || 0) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <span class="text-success fw-bold">
                      ${{ formatNumber(shipping.totalApplied || 0) }}
                    </span>
                  </td>
                  <td class="text-end">
                    <span
                      :class="
                        shipping.totalAmount - shipping.totalApplied > 0
                          ? 'text-warning fw-bold'
                          : 'text-success fw-bold'
                      "
                    >
                      ${{
                        formatNumber(
                          (shipping.totalAmount || 0) -
                            (shipping.totalApplied || 0)
                        )
                      }}
                    </span>
                  </td>
                  <td>
                    <span :class="getStatusClass(shipping.status || 'pending')">
                      {{ getStatusLabel(shipping.status || "pending") }}
                    </span>
                  </td>
                  <td>{{ formatDate(shipping.latestPaymentDate) }}</td>
                  <td>
                    <span class="text-muted">-</span>
                  </td>
                  <td>
                    <span class="text-muted">-</span>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="border-top">
                <td colspan="2" class="fw-bold text-end text-muted">TOTAL:</td>
                <td class="fw-bold text-primary text-end">
                  ${{ formatNumber(getTotalAmount()) }}
                </td>
                <td class="fw-bold text-success text-end">
                  ${{ formatNumber(getTotalApplied()) }}
                </td>
                <td class="fw-bold text-warning text-end">
                  ${{ formatNumber(getTotalRemaining()) }}
                </td>
                <td colspan="4"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination && pagination.totalPages > 1" class="row mt-4">
      <div class="col-12">
        <nav aria-label="Payments pagination">
          <ul class="pagination justify-content-center">
            <li
              class="page-item"
              :class="{ disabled: !pagination.hasPrevPage }"
            >
              <button
                class="page-link"
                @click="changePage(pagination.currentPage - 1)"
                :disabled="!pagination.hasPrevPage"
              >
                Previous
              </button>
            </li>
            <li
              v-for="page in getPageNumbers()"
              :key="page"
              class="page-item"
              :class="{ active: page === pagination.currentPage }"
            >
              <button class="page-link" @click="changePage(page)">
                {{ page }}
              </button>
            </li>
            <li
              class="page-item"
              :class="{ disabled: !pagination.hasNextPage }"
            >
              <button
                class="page-link"
                @click="changePage(pagination.currentPage + 1)"
                :disabled="!pagination.hasNextPage"
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ExportService from "@/services/exportService";
import { formatNumberSync } from "@/utils/numberFormatter";
import { formatDateSync } from "@/utils/dateFormatter";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "PaymentsList",
  components: { ContentSkeleton },
  props: {
    proformId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // Data
      payments: [],
      pagination: {},

      // Loading
      loading: false,

      // Sorting
      sortBy: "createdAt",
      sortOrder: "desc",
    };
  },
  async mounted() {
    await this.loadPayments();
  },
  watch: {
    proformId: {
      immediate: true,
      handler(newProformId) {
        if (newProformId) {
          this.loadPayments();
        }
      },
    },
  },
  methods: {
    async loadPayments() {
      try {
        this.loading = true;

        const params = this.buildQueryParams();
        console.log("🔍 Loading payments with params:", params);

        const response = await wencesApi.get("/api/financial/payments", {
          params,
        });

        if (response.data.payments) {
          // Group payments by proform and shipping similar to CollectsList
          this.payments = this.groupPaymentsByProform(response.data.payments);
          this.pagination = response.data.pagination;
          console.log("🔍 Payments loaded and grouped:", this.payments.length);
          console.log("🔍 First payment group data:", this.payments[0]);
        } else {
          throw new Error(response.data.error || "Failed to load payments");
        }
      } catch (error) {
        console.error("Error loading payments:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    groupPaymentsByProform(payments) {
      const groupedPayments = {};

      payments.forEach((payment) => {
        // Determine if this payment belongs to a proform or shipping
        const proformId = payment.proform?._id || payment.proform;
        const shippingId = payment.shipping?._id || payment.shipping;

        if (proformId) {
          // This is a proform payment
          if (!groupedPayments[proformId]) {
            groupedPayments[proformId] = {
              _id: proformId,
              proformName:
                payment.proform?.name ||
                payment.proformName ||
                "Unknown Proform",
              proform: payment.proform,
              contact: payment.contact,
              payments: [],
              shippings: [],
              totalAmount: 0,
              totalApplied: 0,
              status: "pending",
              latestPaymentDate: null,
              createdAt: payment.createdAt,
            };
          }

          groupedPayments[proformId].payments.push(payment);
          groupedPayments[proformId].totalAmount += payment.amount || 0;
          groupedPayments[proformId].totalApplied += payment.applied || 0;

          // Update status based on payment status
          if (payment.paid) {
            groupedPayments[proformId].status = "paid";
          } 
          if (payment.applied > 0) {
            groupedPayments[proformId].status = "partial";
          }
          if(payment.applied == payment.amount){
            groupedPayments[proformId].status = "paid";
          }

          // Update latest payment date
          if (
            payment.payment_date &&
            (!groupedPayments[proformId].latestPaymentDate ||
              new Date(payment.payment_date) >
                new Date(groupedPayments[proformId].latestPaymentDate))
          ) {
            groupedPayments[proformId].latestPaymentDate = payment.payment_date;
          }
        }

        if (shippingId) {
          // This is a shipping payment
          const parentProformId = payment.proform?._id || payment.proform;

          if (parentProformId && groupedPayments[parentProformId]) {
            // Find or create shipping group
            let shippingGroup = groupedPayments[parentProformId].shippings.find(
              (s) => s._id === shippingId
            );

            if (!shippingGroup) {
              shippingGroup = {
                _id: shippingId,
                proformName:
                  payment.shipping?.name ||
                  payment.shippingName ||
                  "Unknown Shipping",
                shipping: payment.shipping,
                contact: payment.contact,
                payments: [],
                totalAmount: 0,
                totalApplied: 0,
                status: "pending",
                latestPaymentDate: null,
                createdAt: payment.createdAt,
              };
              groupedPayments[parentProformId].shippings.push(shippingGroup);
            }

            shippingGroup.payments.push(payment);
            shippingGroup.totalAmount += payment.amount || 0;
            shippingGroup.totalApplied += payment.applied || 0;

            // Update status based on payment status
            if (payment.paid) {
              shippingGroup.status = "paid";
            } else if (payment.applied > 0) {
              shippingGroup.status = "partial";
            }

            // Update latest payment date
            if (
              payment.payment_date &&
              (!shippingGroup.latestPaymentDate ||
                new Date(payment.payment_date) >
                  new Date(shippingGroup.latestPaymentDate))
            ) {
              shippingGroup.latestPaymentDate = payment.payment_date;
            }
          }
        }
      });

      return Object.values(groupedPayments);
    },

    buildQueryParams() {
      const params = {
        page: this.pagination?.currentPage || 1,
        limit: 50,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        proform: this.proformId, // Filter by specific proform
      };

      return params;
    },

    async applyFilters() {
      if (this.pagination) {
        this.pagination.currentPage = 1; // Reset to first page
      }
      await this.loadPayments();
    },

    async changePage(page) {
      if (this.pagination && page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page;
        await this.loadPayments();
      }
    },

    getPageNumbers() {
      if (!this.pagination) return [];

      const current = this.pagination.currentPage;
      const total = this.pagination.totalPages;
      const pages = [];

      // Show up to 5 pages around current page
      const start = Math.max(1, current - 2);
      const end = Math.min(total, current + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    // Navigation methods
    viewProform(paymentGroup) {
      if (paymentGroup && paymentGroup.proform && paymentGroup.proform._id) {
        this.$router.push(
          `/dynamic-model/proform/view/${paymentGroup.proform._id}`
        );
      }
    },

    viewShipping(shipping) {
      if (shipping && shipping.shipping && shipping.shipping._id) {
        this.$router.push(
          `/dynamic-model/shipping/view/${shipping.shipping._id}`
        );
      }
    },

    viewPayment(payment) {
      if (payment && payment._id) {
        this.$router.push(`/dynamic-model/payment/view/${payment._id}`);
      }
    },

    editPayment(payment) {
      if (payment && payment._id) {
        this.$router.push(`/dynamic-model/payment/edit/${payment._id}`);
      }
    },

    viewContact(contact) {
      if (contact && contact._id) {
        this.$router.push(`/dynamic-model/Contact/view/${contact._id}`);
      }
    },

    // Utility methods
    getContactName(contact) {
      if (!contact) return "-";

      if (typeof contact === "object" && contact.name) {
        return contact.name;
      }

      return contact;
    },

    getBankAccountName(bankAccount) {
      if (!bankAccount) return "-";

      if (typeof bankAccount === "object" && bankAccount.name) {
        return bankAccount.name;
      }

      return bankAccount;
    },

    getStatusClass(status) {
      switch (status) {
        case "paid":
        case "collected":
          return "badge bg-success";
        case "pending":
          return "badge bg-warning";
        case "partial":
          return "badge bg-info";
        default:
          return "badge bg-secondary";
      }
    },

    getStatusLabel(status) {
      switch (status) {
        case "paid":
        case "collected":
          return "Paid";
        case "pending":
          return "Pending";
        case "partial":
          return "Partial";
        default:
          return "Unknown";
      }
    },

    formatNumber(number) {
      if (!number) return "0.00";
      // Siempre mostrar decimales para valores monetarios en payments
      return formatNumberSync(number, null, true);
    },

    formatDate(dateString) {
      return formatDateSync(dateString, "N/A");
    },

    // Total calculation methods
    getTotalAmount() {
      return this.payments.reduce((total, group) => {
        return total + (group.totalAmount || 0);
      }, 0);
    },

    getTotalApplied() {
      return this.payments.reduce((total, group) => {
        return total + (group.totalApplied || 0);
      }, 0);
    },

    getTotalRemaining() {
      return this.payments.reduce((total, group) => {
        return total + ((group.totalAmount || 0) - (group.totalApplied || 0));
      }, 0);
    },

    async exportData() {
      try {
        // Show loading
        Swal.fire({
          title: this.$tn("Preparing Export"),
          text: this.$tn("Loading all data... This may take a moment."),
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Build query params (same as buildQueryParams but for export)
        const params = {
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          proform: this.proformId, // Filter by specific proform
        };

        // Call export endpoint
        const response = await wencesApi.get("/api/financial/payments/export", {
          params,
        });

        if (!response.data.success || !response.data.payments) {
          throw new Error(response.data.error || "Failed to load data for export");
        }

        const allPayments = response.data.payments;

        if (!allPayments || allPayments.length === 0) {
          Swal.close();
          Swal.fire({
            icon: "warning",
            title: this.$tn("No Data"),
            text: this.$tn("No payments to export"),
          });
          return;
        }

        // Group payments by proform (same logic as groupPaymentsByProform)
        const groupedPayments = this.groupPaymentsByProform(allPayments);

        // Prepare data for export
        const data = [];
        
        // Headers
        data.push([
          this.$t("Proform"),
          this.$t("Contact"),
          this.$t("Amount"),
          "Applied",
          this.$t("Remaining to apply"),
          this.$t("Status"),
          this.$t("Payment Date"),
          "Bank Account",
          "Type",
        ]);

        // Data rows - flatten payments structure
        groupedPayments.forEach((paymentGroup) => {
          // Add proform row
          data.push([
            paymentGroup.proformName || "-",
            this.getContactName(paymentGroup.contact),
            this.formatNumber(paymentGroup.totalAmount || 0),
            this.formatNumber(paymentGroup.totalApplied || 0),
            this.formatNumber((paymentGroup.totalAmount || 0) - (paymentGroup.totalApplied || 0)),
            this.getStatusLabel(paymentGroup.status || "pending"),
            this.formatDate(paymentGroup.latestPaymentDate),
            "-",
            "-",
          ]);

          // Add shipping rows
          if (paymentGroup.shippings && paymentGroup.shippings.length > 0) {
            paymentGroup.shippings.forEach((shipping) => {
              data.push([
                `  ${shipping.proformName || "-"}`, // Indent to show hierarchy
                this.getContactName(shipping.contact),
                this.formatNumber(shipping.totalAmount || 0),
                this.formatNumber(shipping.totalApplied || 0),
                this.formatNumber((shipping.totalAmount || 0) - (shipping.totalApplied || 0)),
                this.getStatusLabel(shipping.status || "pending"),
                this.formatDate(shipping.latestPaymentDate),
                "-",
                "-",
              ]);
            });
          }
        });

        // Add summary row
        data.push([]);
        data.push([
          "TOTALS",
          "",
          this.formatNumber(this.getTotalAmount()),
          this.formatNumber(this.getTotalApplied()),
          this.formatNumber(this.getTotalRemaining()),
          "",
          "",
          "",
          "",
        ]);

        Swal.close();

        const fileName = `payments_${new Date().toISOString().split("T")[0]}`;
        await ExportService.exportDataToExcel(data, fileName, "Payments");

        Swal.fire({
          icon: "success",
          title: this.$tn("Export Successful"),
          text: this.$tn("Data exported successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error exporting data:", error);
        Swal.close();
        Swal.fire({
          icon: "error",
          title: this.$tn("Export Error"),
          text: error.response?.data?.error || this.$tn("Failed to export data"),
        });
      }
    },
  },
};
</script>

<style scoped></style>
